import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Upload,
  Image,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import api from '../services/api';

const Empresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchEmpresas();
  }, []);

  const fetchEmpresas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      message.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadLogo = async (file) => {
    const isImage = file.type?.startsWith('image/');
    if (!isImage) {
      message.error('Solo se permiten imágenes');
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > 5) {
      message.error('El logo debe ser menor a 5MB');
      return Upload.LIST_IGNORE;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('imagenes[]', file);
      formData.append('tipo', 'empresas');
      formData.append('field_name', 'imagenes');

      const response = await api.post('/empresas/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const url = response.data?.files?.[0]?.url || response.data?.urls?.[0];
      if (!url) {
        throw new Error('No se recibió la URL del logo');
      }
      setLogoUrl(url);
      form.setFieldsValue({ logo_url: url });
      message.success('Logo subido');
    } catch (error) {
      message.error(error.message || 'Error al subir logo');
    } finally {
      setUploadingLogo(false);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    try {
      const payload = { ...values, logo_url: logoUrl || values.logo_url || null };
      if (editingEmpresa) {
        await api.put(`/empresas/${editingEmpresa.id}`, payload);
        message.success('Empresa actualizada exitosamente');
      } else {
        await api.post('/empresas', payload);
        message.success('Empresa creada exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingEmpresa(null);
      setLogoUrl(null);
      fetchEmpresas();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleEdit = (record) => {
    setEditingEmpresa(record);
    setLogoUrl(record.logo_url || null);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/empresas/${id}`);
      message.success('Empresa eliminada exitosamente');
      fetchEmpresas();
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Logo',
      dataIndex: 'logo_url',
      key: 'logo_url',
      width: 80,
      render: (url) =>
        url ? <Image src={url} width={40} height={40} style={{ objectFit: 'contain' }} /> : '-',
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'RUC',
      dataIndex: 'ruc',
      key: 'ruc',
    },
    {
      title: 'Teléfono',
      dataIndex: 'telefono',
      key: 'telefono',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Contacto',
      dataIndex: 'contacto',
      key: 'contacto',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          activa: 'green',
          inactiva: 'red',
        };
        return <Tag color={colors[estado]}>{estado === 'cancelado' ? 'ANULADO' : estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta empresa?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Empresas"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingEmpresa(null);
              setLogoUrl(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Nueva Empresa
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={empresas}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingEmpresa(null);
          setLogoUrl(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: 'Ingresa el nombre de la empresa' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="ruc" label="RUC">
            <Input />
          </Form.Item>
          <Form.Item name="direccion" label="Dirección">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="telefono" label="Teléfono">
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Email inválido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="contacto" label="Contacto">
            <Input />
          </Form.Item>
          <Form.Item name="logo_url" hidden>
            <Input />
          </Form.Item>
          <Form.Item label="Logo (para boletas)" extra="PNG/JPG, máx 5MB">
            <Space direction="vertical">
              {logoUrl && (
                <Image src={logoUrl} width={120} style={{ objectFit: 'contain', background: '#f5f5f5' }} />
              )}
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleUploadLogo}
              >
                <Button icon={<UploadOutlined />} loading={uploadingLogo}>
                  {logoUrl ? 'Cambiar Logo' : 'Subir Logo'}
                </Button>
              </Upload>
              {logoUrl && (
                <Button
                  type="link"
                  danger
                  onClick={() => {
                    setLogoUrl(null);
                    form.setFieldsValue({ logo_url: null });
                  }}
                >
                  Quitar logo
                </Button>
              )}
            </Space>
          </Form.Item>
          <Form.Item name="estado" label="Estado" initialValue="activa">
            <Select>
              <Select.Option value="activa">Activa</Select.Option>
              <Select.Option value="inactiva">Inactiva</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingEmpresa ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingEmpresa(null);
                  setLogoUrl(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Empresas;
